import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";
import "./modules.css";
import { globalVariables } from "helper/globalVariables";
import { BottomButtons } from "shared/Components/bottomButtons";
import { Header } from "shared/Components/header";
import { TextInput } from "shared/Components/textInput";
import { TextArea } from "shared/Components/textarea";
import { registerMemberResolver } from "validators/UserValidation";
import { FileUpload } from "shared/Components/fileUpload";
import { useNavigate } from "react-router-dom";
import { useUploadImage } from "redux/hooks";
import { client } from "helper/apolloConfig";
import {
  registerMemberMutation,
  updateMemberProfileMutation,
} from "redux/features/memberSlice/memberGql";
import { setMemberDetails } from "redux/features/memberSlice/memberSlice";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { useDispatch, useSelector } from "react-redux";
import { StepsHeader } from "shared/Components/stepsHeader";
import { PhoneInput } from "shared/Components/phoneInput";
import { SearchLocation } from "shared/Components/searchLocation";
import { OutlineButton } from "shared/Components/outlineButton";
import { logout } from "helper/icons";
import { useLogout } from "helper/logout";

export const RegisterMember = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: registerMemberResolver,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      location: "",
      city: "",
      state: "",
      phoneNumber: "",
      emergencyContactName: "",
      emergencyContactNumber: "",
      zip: "",
    },
  });

  const modalRef = useRef();
  const dispatch = useDispatch();
  const { onUploadImage, imageUrl } = useUploadImage();
  const [memberImage, setMemberImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [checkValidation, setCheckValidation] = useState(false);
  const { auth } = useSelector((state) => state);
  const { onLogout } = useLogout();

  useEffect(() => {
    if (imageUrl) {
      setMemberImage(imageUrl);
    }
  }, [imageUrl]);

  useLayoutEffect(() => {
    getMemberProfile();
  }, []);

  const getMemberProfile = async () => {
    if (auth?.memberReducer?.memberDetails) {
      const memberProfile = auth?.memberReducer?.memberDetails;
      setValue("firstName", memberProfile?.first_name);
      setValue("lastName", memberProfile?.last_name);
      setValue("email", memberProfile?.email);
      setValue("location", memberProfile?.location_address);
      setValue("phoneNumber", memberProfile?.phone_number);
      setValue("emergencyContactName", memberProfile?.emergency_contact_name);
      setValue("zip", memberProfile?.zipCode);
      setValue(
        "emergencyContactNumber",
        memberProfile?.emergency_contact_number
      );
      setMemberImage(memberProfile?.profile);
      setCityFilter(memberProfile?.city);
      setStateFilter(memberProfile?.state);
    }
  };

  const onValidate = () => {
    if (cityFilter && stateFilter) {
      setCheckValidation(false);
    } else {
      setCheckValidation(true);
    }
  };

  const onHandleSubmit = async (credentials) => {
    // Guard: city and state are managed outside react-hook-form, validate them here
    if (!cityFilter || !stateFilter) {
      setCheckValidation(true);
      return;
    }

    setLoading(true);

    const payload = {
      first_name: credentials?.firstName,
      last_name: credentials?.lastName,
      location_address: credentials?.location,
      phone_number: credentials?.phoneNumber,
      emergency_contact_name: credentials?.emergencyContactName,
      emergency_contact_number: credentials?.emergencyContactNumber,
      // email: credentials?.email,
      zipCode: credentials?.zip,
      city: cityFilter,
      state: stateFilter,
    };

    if (memberImage) {
      payload.profile = memberImage;
    }

    try {
      const result = await client.mutate({
        mutation: auth?.memberReducer?.memberDetails
          ? updateMemberProfileMutation
          : registerMemberMutation,
        variables: { data: payload },
      });

      if (
        auth?.memberReducer?.memberDetails
          ? result?.data?.updateMyMemberProfile
          : result?.data?.registerMember
      ) {
        dispatch(
          setMemberDetails(
            auth?.memberReducer?.memberDetails
              ? result?.data?.updateMyMemberProfile
              : result?.data?.registerMember
          )
        );
        navigate("/member/registerDog");
      }
      setLoading(false);
    } catch (error) {
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: globalVariables.error,
        desc: error?.message,
        type: "pending",
      });
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="flex justify-center mt-8 flex-col items-center ">
        <span className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">
          {globalVariables.registerMember}
        </span>
        <StepsHeader
          step={1}
          labels={[
            globalVariables.registerMember,
            globalVariables.registerDog,
            globalVariables.makePayment,
          ]}
        />

        <div className="mt-10" />
        <span className="text-xl font-semibold text-gray-900 mb-1">
          {globalVariables.addMemberDetails}
        </span>
        <span className="text-base font-regular text-gray-500 mb-6 max-[400px]:w-[90%]">
          {globalVariables.thereRegistrationFee}
        </span>
        <FileUpload
          onSetImage={(img) => onUploadImage(img)}
          defaultImage={memberImage}
          containerWidth="w-[90%]"
        />
        <div className="mt-6" />
        <div className="w-full items-center flex flex-col justify-center">
          <form
            className="w-full items-center flex flex-col"
            onSubmit={handleSubmit(onHandleSubmit)}
          >
            <div className="max-[400px]:w-[90%]">
              <TextInput
                register={register}
                name="firstName"
                placeholder={globalVariables.enterFirstName}
                errorMsg={errors.firstName?.message}
              />
              <div className="mt-5" />
              <TextInput
                register={register}
                name="lastName"
                placeholder={globalVariables.enterLastName}
                errorMsg={errors.lastName?.message}
              />
              <div className="mt-5" />
              {/* <TextInput
                register={register}
                name="email"
                placeholder={globalVariables.enterEmailAddress}
                errorMsg={errors.email?.message}
                textTransform="normal-case"
              /> */}
              <div className="mt-5" />
              <TextArea
                register={register}
                name="location"
                placeholder={globalVariables.enterLocationAddress}
                errorMsg={errors.location?.message}
              />
              <div className="mt-5" />
              <SearchLocation
                width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
                name="State"
                types={["(regions)"]}
                placeholder={globalVariables.state}
                onSelectLocation={(location) => {
                  setStateFilter(location);
                  setValue("state", location);
                  onValidate();
                }}
                errorMsg={errors.state?.message || (
                  checkValidation &&
                  !stateFilter &&
                  globalVariables.pleaseSelectState
                )}
                defaultValue={stateFilter}
              />
              <div className="mt-5" />
              <SearchLocation
                width={"w-[358px] lg:w-[384px] max-[400px]:w-[100%]"}
                name="city"
                placeholder={globalVariables.city}
                types={["(cities)"]}
                onSelectLocation={(location) => {
                  setCityFilter(location);
                  setValue("city", location);
                  onValidate();
                }}
                errorMsg={errors.city?.message || (
                  checkValidation &&
                  !cityFilter &&
                  globalVariables.pleaseSelectCity
                )}
                helperText={globalVariables.cityHelperText}
                defaultValue={cityFilter}
                searchBy={stateFilter}
              />
              <div className="mt-5" />
              <TextInput
                register={register}
                name="zip"
                placeholder={globalVariables.zip}
                errorMsg={errors.zip?.message}
                type="number"
              />
              <div className="mt-5" />
              <PhoneInput
                name="phoneNumber"
                placeholder={globalVariables.enterPhoneNumber}
                errorMsg={errors.phoneNumber?.message}
                value={watch("phoneNumber")}
                onChange={(e) => setValue("phoneNumber", e.target.value)}
              />
              <div className="mt-5" />
              <TextInput
                register={register}
                name="emergencyContactName"
                placeholder={globalVariables.enterEmergencyContactName}
                errorMsg={errors.emergencyContactName?.message}
              />
              <div className="mt-5" />
              <PhoneInput
                name="emergencyContactNumber"
                placeholder={globalVariables.enterEmergencyPhoneNumber}
                errorMsg={errors.emergencyContactNumber?.message}
                value={watch("emergencyContactNumber")}
                onChange={(e) =>
                  setValue("emergencyContactNumber", e.target.value)
                }
              />
            </div>
            <div className="mb-8" />
            <BottomButtons
              icon={logout}
              onTapNo={onLogout}
              outlineLabel={globalVariables.logout}
              onTapYes={onValidate}
              disabled={loading}
            />
          </form>
        </div>
      </div>
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={modalRef}
      />
    </div>
  );
};
